/** Domain types shared across the opncue app. */

import type { UserAvatarConfig } from "@/lib/avatar-config";
import type { ExportRange } from "@/lib/export-request";

export type { UserAvatarConfig };

export type ID = string;

export const CONVERSATION_DECK_SERVER_CATEGORIES = [
  "start",
  "keep_going",
  "fun",
  "get_to_know",
  "go_deeper",
  "make_a_plan",
  "wrap_up",
] as const;

export type ConversationDeckServerCategory =
  (typeof CONVERSATION_DECK_SERVER_CATEGORIES)[number];

/**
 * The decks that need Opncue Plus. A MIRROR of
 * `backend/api/src/modules/conversation-deck/conversation-deck.types.ts`,
 * pinned by `tests/conversation-deck-plus.test.js`.
 *
 * This list decides what the UI draws — which pills wear a lock and open the
 * upsell. It decides NOTHING about access: a free account is never sent these
 * helpers, so editing this file cannot unlock anything. That separation is
 * deliberate, because the deck is cached on the device for offline use, and a
 * paywall that only exists in the client is a drawing of a paywall.
 */
export const CONVERSATION_DECK_PLUS_CATEGORIES = [
  "go_deeper",
  "make_a_plan",
  "wrap_up",
] as const satisfies readonly ConversationDeckServerCategory[];

export function isPlusDeckCategory(
  category: ConversationDeckServerCategory,
): boolean {
  return (CONVERSATION_DECK_PLUS_CATEGORIES as readonly string[]).includes(
    category,
  );
}

/**
 * Every deck is a real server category now.
 *
 * There used to be a client-only `unstick` that drew from start/keep_going/fun
 * — it had no bank of its own and no server category, so it only ever
 * reshuffled decks the user already had. It was removed rather than paywalled
 * or kept: a deck that is three other decks in a coat is a menu entry, not
 * content.
 */
export type ConversationDeckCategory = ConversationDeckServerCategory;

export type ConversationDeckHelper = {
  id: string;
  category: ConversationDeckServerCategory;
  main: string;
  possibleFollowUp: string;
};

export type ConversationDeckBootstrapResponse = {
  bankVersion: number;
  schemaVersion: number;
  revision: number;
  helpers: ConversationDeckHelper[];
  invalidated: boolean;
};

export type ConversationDeckRefillResponse = {
  bankVersion: number;
  schemaVersion: number;
  revision: number;
  category: ConversationDeckServerCategory;
  helpers: ConversationDeckHelper[];
  invalidated: boolean;
};

/** A controlled mood the user can drop into their Mood Jar. */
export type MoodCatalogItem = {
  id: string;
  emoji: string;
  valence: number;
  energy: "low" | "medium" | "high";
  social: "low" | "guarded" | "open" | "high";
  gentle: boolean;
};

export type MoodContextCatalogItem = {
  id: string;
  emoji: string;
};

export type MoodCatalog = {
  moods: MoodCatalogItem[];
  contexts: MoodContextCatalogItem[];
};

/** One Mood Jar entry. */
export type MoodJarEntry = {
  id: ID;
  moodId: string;
  moodOther: string | null;
  intensity: number;
  contextId: string | null;
  contextOther: string | null;
  createdAt: string;
};

export type MoodJarSummary = {
  periodKey: string;
  entryCount: number;
  activeDays: number;
};

/** Lifecycle of a completed month's Monthly Vibe Card. */
export type MonthlyVibeCardStatus =
  | "collecting"
  | "insufficient_data"
  | "ready"
  | "generating"
  | "available"
  /**
   * The month passed without a card and its reveal window has closed, so the
   * jar behind it was cleared. Distinct from `insufficient_data` — that one is
   * the user not logging enough, this one is us clearing up.
   */
  | "expired"
  | "failed"
  | "unavailable";

/** The three approved share-card concepts. */
export type MonthlyVibeTemplateId =
  | "monthly_vibe_verdict"
  | "month_in_one_pic"
  | "monthly_vibe_receipt";

/**
 * The complete immutable material needed to render, save and share a card.
 * It contains no source analytics and requires no remote image.
 */
export type MonthlyVibeCardContent = {
  periodKey: string;
  headline: string;
  subline: string;
  tone: "dry" | "warm" | "chaotic" | "mixed";
  templateId: MonthlyVibeTemplateId;
};

export type MonthlyVibeCard = {
  periodKey: string;
  status: MonthlyVibeCardStatus;
  cardId: ID | null;
  createdAt: string | null;
  completedAt: string | null;
  /** Present only when status is "available". */
  content: MonthlyVibeCardContent | null;
  /** Collecting/eligibility progress — never rendered on the final card. */
  eligibility: {
    entryCount: number;
    activeDays: number;
    minEntries: number;
    minActiveDays: number;
  } | null;
};

export type MonthlyVibeCardStatusResponse = {
  enabled: boolean;
  current: MoodJarSummary;
  latest: MonthlyVibeCard;
};

export type VibeTag = {
  emoji: string;
  label: string;
};

export type Mode = "meet" | "work" | "networking" | "language" | "curious";


export type NotificationPreferences = {
  muteAll: boolean;
  newPeople: boolean;
  cues: boolean;
  messages: boolean;
  /** Nudge two days before a small goal's due date. */
  goalReminders: boolean;
};

export type User = {
  id: ID;
  email?: string;
  /**
   * When this account was deactivated, or null while it is active. Sent only
   * for the signed-in user — nobody else can see it, or see them.
   *
   * Deactivated means socially switched off: no cues, no messages, no Small
   * Groups, no profile anyone else can open. Practice, goals and reflections
   * are untouched, which is what makes it the lighter alternative to deleting.
   */
  deactivatedAt?: string | null;
  /**
   * When my own Open-to-chat signal lapses, or null when I am not open.
   *
   * Sent only for the signed-in user, like `deactivatedAt`. It arrives with
   * the session bootstrap that already reads this record, which is what lets
   * the Open tab show the right toggle state without a request of its own.
   *
   * A LEASE, not a flag: the app compares it against the clock rather than
   * waiting to be told it expired, so a phone that was asleep through the
   * lapse still reads it correctly on wake.
   */
  openToChatUntil?: string | null;
  locale?: string | null;
  displayName: string;
  /**
   * The EFFECTIVE tier, already resolved by the server — a referral-granted
   * Plus arrives here as "plus". Gate features on this; it is the one
   * definition of who has Plus.
   */
  plan?: "free" | "plus";
  /**
   * WHY they have Plus, or null on free.
   *
   * `plan` deliberately cannot answer this, which is the point: someone on a
   * grant has full access but nothing to manage or cancel, so offering them
   * "Manage subscription" opens a page about a purchase they never made.
   * Use this ONLY for that distinction — never to gate a feature.
   */
  plusSource?: "subscription" | "grant" | null;
  /** ISO expiry of a temporary Plus grant (e.g. a referral reward), else null. */
  plusGrantUntil?: string | null;
  supporterBadge?: boolean;
  avatarUrl?: string | null;
  avatarSeed?: string;
  /** Structured illustrated-avatar config; null until customized. */
  avatarConfig?: UserAvatarConfig | null;
  vibe?: VibeTag | null;
  mode?: Mode | null;
  onboardingComplete?: boolean;
  headline?: string | null;
  about?: string | null;
  /**
   * Read-only. Derived server-side from `dateOfBirth` on every response, and
   * NOT writable — `users.age` was dropped on 2026-08-18 because a stored copy
   * went stale on the user's next birthday. Sending it in a profile patch is
   * silently ignored.
   */
  age?: number | null;
  ageBand?: string | null;
  dateOfBirth?: string | null;
  interests?: string[] | null;
  additionalInterests?: string[] | null;
  lookingFor?: string[] | null;
  additionalFriendshipGoals?: string[] | null;
  languages?: string[] | null;
  religion?: string | null;
  politics?: string | null;
  gender?: string | null;
  /** People-discovery radius in metres, [500, 10000]. Server clamps on write. */
  nearbyRadiusM?: number | null;
  visibilityPreferences?: VisibilityPreferences | null;
  /**
   * The saved nearby filters, as the server holds them.
   *
   * Persisted rather than screen state because the server has to honour them
   * when the app is closed: the nearby alert's promise is "someone you could cue
   * is here", and without the filters it would wake someone about a person they
   * had explicitly excluded. See `PeopleFilterPreferences` on the API.
   *
   * Age is a band range, never raw ages — filtering finer than the band we
   * publish would let someone read an exact age by narrowing and watching.
   */
  peopleFilters?: PeopleFilterPreferences | null;
  notificationPreferences?: NotificationPreferences | null;
  reflectionFrequency?: ReflectionFrequency;
  /** Custom-reminder weekdays: JS getDay() values, 0 = Sunday … 6 = Saturday. */
  reflectionReminderDays?: number[] | null;
  /** Custom-reminder local time as 24h "HH:MM". */
  reflectionReminderTime?: string | null;
  /** Device IANA time zone; drives reminder-email local time and Mood Jar. */
  timezone?: string | null;
  weeklyEmailOptIn?: boolean;
};

/**
 * Audience preferences — who the user is willing to be visible to. Distinct
 * from profile details and from the nearby "filter" (who the user wants to
 * see). A null/empty dimension means no restriction.
 */
export type VisibilityPreferences = {
  ageMin: number | null;
  ageMax: number | null;
  genders: string[] | null;
};

/**
 * When the reflection reminder fires.
 *   evenings → every evening at 8pm (local)
 *   weekly   → Saturday 8pm (local)
 *   custom   → the user's chosen weekdays + time
 *   off      → never
 */
export type ReflectionFrequency = "evenings" | "weekly" | "custom" | "off";

/** The moment an auto-prompt fires for (maps to a where/subject preset). */
export type ReflectionContext =
  | "cue"
  | "conversation"
  | "circle"
  | "venue"
  | "day";

export type ReflectionWhere = "opncue" | "somewhere_else";

export type ReflectionSubject =
  | "a_cue"
  | "a_conversation"
  | "a_group"
  | "said_hi"
  | "started_conversation"
  | "joined_group"
  | "joined_in"
  | "went_somewhere_social"
  | "replied_to_someone"
  | "reached_out"
  | "met_someone_new"
  | "made_plan"
  | "tried_something_difficult"
  | "stepped_away"
  | "showed_up"
  | "rested"
  | "custom";

export type ReflectionFeeling =
  | "good"
  | "okay"
  | "energised"
  | "awkward"
  | "hard_but_tried"
  | "proud"
  | "comfortable"
  | "curious"
  | "unsure"
  | "drained"
  | "disappointed"
  | "not_my_vibe"
  | "quiet"
  | "custom";

export type ReflectionOutcome =
  | "made_connection"
  | "clicked"
  | "practiced"
  | "learned_something"
  | "more_comfortable_than_expected"
  | "nervous_but_stayed"
  | "learned_about_them"
  | "learned_about_myself"
  | "one_sided"
  | "not_my_vibe"
  | "rested"
  | "no_outcome_yet"
  | "nothing_in_particular"
  | "custom";

export type Reflection = {
  id: ID;
  where: ReflectionWhere | null;
  subject: ReflectionSubject | null;
  feeling: ReflectionFeeling | null;
  outcome: ReflectionOutcome | null;
  note: string | null;
  createdAt: string;
};

export type ReflectionInput = {
  where: ReflectionWhere;
  subject: ReflectionSubject;
  feeling: ReflectionFeeling;
  outcome?: ReflectionOutcome;
  note?: string;
};

/** Filters for browsing the journal. Everything is optional — no filter means all. */
export type ReflectionHistoryQuery = {
  where?: ReflectionWhere;
  subject?: ReflectionSubject;
  feeling?: ReflectionFeeling;
  outcome?: ReflectionOutcome;
  /** Inclusive ISO bounds on when the reflection was logged. */
  from?: string;
  to?: string;
  /** Free-text search over the note. */
  q?: string;
  /** Opaque — only ever a `nextCursor` handed back by a previous page. */
  cursor?: string;
  limit?: number;
};

/** One page of reflection history. `nextCursor` is null on the last page. */
export type ReflectionPage = {
  items: Reflection[];
  nextCursor: string | null;
};

export type UserPlan = "free" | "plus";

/** This month's reflection allowance + the plan's journaling limits. */
export type ReflectionQuota = {
  plan: UserPlan;
  used: number;
  /** Reflections logged so far in the user's local calendar day. */
  usedToday: number;
  /** Monthly allowance; `null` means unlimited (Plus). */
  limit: number | null;
  /** Remaining this month; `null` when unlimited. */
  remaining: number | null;
  resetsAt: string;
  noteMaxLength: number;
  canExport: boolean;
};

/** Full data export payload (Plus only). */
// Re-exported rather than redeclared: these describe the request the export
// screen builds, and they live beside that logic in a module with no imports so
// a plain Node test can load it. Two copies would drift.
export type {
  ExportRange,
  ExportSection,
  ReflectionExportOptions,
} from "@/lib/export-request";

export type ReflectionExport = {
  generatedAt: string;
  plan: UserPlan;
  /** The range the server actually applied. */
  range: ExportRange;
  /** Null when the growth-stats section was not requested. */
  totals: GrowthSummary | null;
  weeklySummaries: TrendPoint[];
  reflections: Reflection[];
  /** The journal was longer than one export can carry; narrow the range. */
  reflectionsTruncated: boolean;
};

/** One bucket for the trends chart (a day or a week). */
export type TrendPoint = {
  /** ISO start of the bucket (a day or a week). */
  weekStart: string;
  reflections: number;
  conversationsStarted: number;
  groupsJoined: number;
  meetupMoments: number;
  cuesSent: number;
  goalsCompleted: number;
  goalsRemoved: number;
  goalsExpired: number;
};

export type SocialGoal = {
  id: ID;
  text: string;
  done: boolean;
  archived: boolean;
  archivedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  /** Date-only ("YYYY-MM-DD") the goal is due, or null if open-ended. */
  dueDate: string | null;
  /** Optional private notes attached to the goal, max 5 notes. */
  notes: string[];
};

// ---- Business "spots" ----

export type SpotStatus = "pending" | "approved" | "rejected";

export type SpotStats = {
  conversationsStarted: number;
  groupsFormed: number;
  meetupMoments: number;
  becameVisible: number;
};

/** Owner-facing spot (business dashboard). */
export type OwnerSpot = {
  id: ID;
  slug: string;
  status: SpotStatus;
  venueId: ID;
  venueName: string;
  venueAddress: string | null;
  welcomeNote: string | null;
  meetupSpots: string[];
  stats: SpotStats;
  createdAt: string;
};

/** Public spot page (no owner/identity info). */
export type PublicSpot = {
  slug: string;
  venueName: string;
  venueAddress: string | null;
  welcomeNote: string | null;
  meetupSpots: string[];
  stats: SpotStats;
};

export type AdminClaim = {
  id: ID;
  slug: string;
  status: SpotStatus;
  venueName: string;
  venueAddress: string | null;
  ownerEmail: string | null;
  createdAt: string;
};

/** Per-user growth totals over a rolling window (default 7 days). */
export type GrowthSummary = {
  rangeDays: number;
  cuesSent: number;
  cuesReceived: number;
  conversationsStarted: number;
  conversationsEnded: number;
  groupsJoined: number;
  meetupMoments: number;
  reflections: number;
  /** Goals checked off in the window. */
  goalsCompleted: number;
  /** Goals deleted in the window. */
  goalsRemoved: number;
  /** Open goals whose due date has passed (not done, not removed). */
  goalsExpired: number;
};

export type ProgressCountBreakdown = {
  key: string;
  count: number;
};

export type ProgressTrendPoint = {
  bucketStart: string;
  practice: number;
  goals: number;
  goalsStarted: number;
  goalsCompleted: number;
  goalsArchived: number;
  reflections: number;
  activity: number;
  cuesSent: number;
  conversations: number;
  groups: number;
  meetups: number;
};

export type ProgressDashboard = {
  rangeDays: 7 | 30;
  rangeStart: string;
  rangeEnd: string;
  practice: {
    completed: number;
    activePractice: number;
    skillsPractised: number;
    mostPractised: { key: string; label: string; count: number } | null;
    categories: { key: string; label: string; count: number }[];
    recent: {
      id: string;
      categoryKey: string;
      categoryLabel: string;
      completedAt: string;
    }[];
  };
  goals: {
    /** Goals created inside the selected range. */
    started: number;
    /** Goals completed inside the selected range. */
    completed: number;
    archived: number;
    removed: number;
    /** Open goals right now — deliberately not filtered by the range. */
    activeNow: number;
    completedGoals: { id: string; title: string; completedAt: string }[];
    /** The list above is a capped window; `completed` remains the true count. */
    completedGoalsHasMore: boolean;
  };
  reflections: {
    total: number;
    insideOpncue: number;
    outsideOpncue: number;
    bySubject: ProgressCountBreakdown[];
    byFeeling: ProgressCountBreakdown[];
    byOutcome: ProgressCountBreakdown[];
    recent: {
      id: string;
      where: ReflectionWhere | null;
      subject: ReflectionSubject | null;
      feeling: ReflectionFeeling | null;
      outcome: ReflectionOutcome | null;
      createdAt: string;
    }[];
  };
  activity: {
    total: number;
    cuesSent: number;
    conversations: number;
    groups: number;
    meetups: number;
    cuesReceived: number;
    recent: {
      type:
        | "cue_sent"
        | "conversation_started"
        | "group_joined"
        | "meetup_moment";
      occurredAt: string;
    }[];
  };
  trends: {
    unit: "day" | "week";
    points: ProgressTrendPoint[];
  };
};

export type AuthSession = {
  accessToken: string;
  refreshToken: string;
  sessionId?: string;
  userId: ID;
};

export type Venue = {
  id: ID;
  name: string;
  /**
   * Whether `name` is a real place or a placeholder.
   *
   * A space with no name yet is still a real space — people are grouped by it
   * and cued in it — but "Near {name}" is a sentence that only works about a
   * name. Optional because an older API does not send it; absent is treated as
   * named, which is what every real place is.
   */
  named?: boolean;
  /**
   * Whether this is a real place two people can both be "at", or the anonymous
   * ~157 m grid cell the server falls back to when nobody is near one.
   *
   * Same Space needs exactly this and `named` does not answer it: a cell gets
   * reverse-geocoded and can wear a real name, while the server still refuses
   * it (`grid_key IS NULL`). Keying the mode off `named` would offer "see who
   * else is here" on a stretch of pavement and return nobody, forever.
   *
   * Optional because an older API does not send it; absent reads as NOT
   * shared, so the mode hides rather than appearing and never working.
   */
  shared?: boolean;
  category?: string;
  distanceMeters?: number;
};

export type SmallGroupMember = {
  id: ID;
  displayName: string;
  avatarSeed?: string | null;
  avatarConfig?: UserAvatarConfig | null;
  ageBand?: string | null;
  readyToMeet: boolean;
  isHost?: boolean;
  /**
   * Whether THIS member has agreed that the group may cue them.
   *
   * The member's own setting, not "can I cue them" — the server deliberately
   * does not compute the latter, because it would have to fold in blocks, pair
   * cooldowns and conversation history, every one of which is a fact about the
   * VIEWER that a roster must not disclose. A Cue action that greyed out for a
   * block would tell the blocker's target they had been blocked.
   *
   * So the row offers a Cue and the server may still refuse it. On the viewer's
   * own row this is what the Cue Me / Join Only toggle reads.
   *
   * Optional because a server built before the group cue door omits it; absent
   * reads as false, which offers nothing — the safe direction.
   */
  allowMemberCues?: boolean;
  /**
   * The viewer's live conversation with this member, if there is one. Turns the
   * row's Cue action into Message. Null on the viewer's own row.
   */
  chatId?: ID | null;
};

export type SmallGroup = {
  id: ID;
  hostUserId?: ID;
  name: string;
  vibe: string;
  ageRange: string | null;
  prompt: string | null;
  maxMembers: number;
  /** How close you must be to JOIN — the host's choice, in metres. */
  radiusM: number;
  memberCount: number;
  minutesLeft: number;
  expiresAt: string;
  isMember: boolean;
  /**
   * active = running, joinable, writable. inactive = ended or expired —
   * read-only history for members. Optional because a server built before the
   * lifecycle existed omits it; absent reads as "active", which only ever
   * errs toward the old behaviour.
   */
  state?: "active" | "inactive";
  viewerMembershipStatus?:
    | "active"
    | "left"
    | "host_removed"
    | "removed_for_safety"
    | "none";
  members: SmallGroupMember[];
};

export type SmallGroupInbox = SmallGroup & {
  unreadCount: number;
  lastMessage: string | null;
  /** Set only when the last line is a system message; render it, not `lastMessage`. */
  lastMessageKey?: string | null;
  lastMessageParams?: Record<string, string> | null;
  lastMessageAt: string | null;
  /**
   * The row's sort key: latest activity this viewer can see, or when the group
   * appeared in their inbox — the same semantics as `ChatSession.updatedAt`,
   * so the inbox orders both kinds of row by one rule.
   */
  updatedAt: string;
};

/** One page of the inactive section, shaped like the chat inbox's pages. */
export type SmallGroupInboxPage = {
  groups: SmallGroupInbox[];
  /** Opaque. Hand it back untouched for the next page; null means the end. */
  nextCursor: string | null;
  /** The section's true size — the tab badge announces it as a total. */
  totalCount: number;
};

/**
 * The group inbox, in the two sections the tabs render. ACTIVE is whole — a
 * finite plan limit bounds concurrent membership — while INACTIVE is history
 * that nothing prunes, so it pages.
 */
export type SmallGroupInboxSections = {
  active: SmallGroupInbox[];
  inactive: SmallGroupInboxPage;
};

export type SmallGroupMessage = {
  id: ID;
  groupId: ID;
  senderId: ID | null;
  senderName: string | null;
  kind: "user" | "system";
  /**
   * English. For a SYSTEM message this is the fallback — render `systemKey`
   * when it is present. Rows written before the server stored keys have only
   * this, and they stay English.
   */
  content: string;
  /** Which system line this is, for `systemMessageText` to render. */
  systemKey?: string | null;
  /** Its placeholder values — a display name, a suggested spot. Data, not copy. */
  systemParams?: Record<string, string> | null;
  timestamp: string;
  /** Set only on bubbles the outbox still holds — see `ChatMessage.pending`. */
  pending?: boolean;
  /** See `ChatMessage.pendingSlow`. */
  pendingSlow?: boolean;
  /** See `ChatMessage.clientMessageId`. */
  clientMessageId?: string | null;
};

export type CreateSmallGroupInput = {
  name: string;
  vibe: string;
  /**
   * The group's age range, as two band floors from `lib/age-bands.ts`.
   *
   * Was a free string, which the API accepted without checking it was a range
   * at all and then never read. It is a rule now: it gates joining and hides
   * groups the viewer cannot join, so it has to be a value the server can
   * evaluate. Both or neither — a half range is no range.
   */
  ageBandMinFloor?: number;
  ageBandMaxFloor?: number;
  maxMembers: number;
  durationMinutes: number;
  prompt?: string;
  gpsLat?: number;
  gpsLng?: number;
  /**
   * Metres of uncertainty in the fix above, and how old it already was.
   *
   * Creating a group checks the host in, so it is a coordinate write like any
   * other. Sending the position without these two stored `accuracy_meters =
   * NULL` and dated the fix "now" — which every radius rule reads as "proves
   * nothing", making the host undiscoverable in both directions the moment they
   * created a group.
   */
  gpsAccuracy?: number;
  gpsAgeMs?: number;
  /** How close someone must be to join, in metres. */
  radiusM: number;
};

/**
 * What an invite link resolves to before joining.
 *
 * Deliberately not a `SmallGroup`: there is no member list and no host id,
 * because a link can be forwarded anywhere. It says the group exists and what
 * it is for — never who is in it.
 */
export type SmallGroupInvite = {
  id: ID;
  name: string;
  vibe: string;
  ageRange: string | null;
  prompt: string | null;
  maxMembers: number;
  memberCount: number;
  radiusM: number;
  minutesLeft: number;
  expiresAt: string;
  isMember: boolean;
  /** Null when the viewer may join right now. */
  block:
    | "out_of_range"
    | "full"
    | "ended"
    | "no_location"
    | "not_visible"
    | "removed"
    | null;
};

export type PresenceStatus = "open" | "working" | "new" | "away";

/**
 * How far someone's identity has been verified. Server-owned: the client only
 * ever reads it, and nothing the user can edit contributes to it.
 *
 * A `person` tier (liveness-only) was once declared here and later removed on
 * both sides — the server's own enum is the same two values. See
 * `verified-badge.tsx` for why the one remaining tier is all there is.
 */
/* `VerificationState` is gone with the user-initiated flow (owner's rule,
 * 2026-09-03): users do not request age verification — Opncue requests it of
 * specific users. GET /me/verification still exists server-side for the
 * targeted flow to read when it is mounted. */

export type NearbyUser = {
  id: ID;
  displayName: string;
  venueTag?: string | null;
  avatarSeed: string;
  avatarConfig?: UserAvatarConfig | null;
  status: PresenceStatus;
  vibe?: VibeTag | null;
  ageBand?: string | null;
  headline?: string | null;
  interests?: string[] | null;
  lookingFor?: string[] | null;
  about?: string | null;
  languages?: string[] | null;
  /**
   * Optional so a client running against an older API renders no badge rather
   * than crashing — absent and `none` must mean the same thing everywhere.
   */
};

export type PeopleFilterPreferences = {
  ageBandMinFloor: number | null;
  ageBandMaxFloor: number | null;
  language: string | null;
  friendshipGoal: string | null;
};

/**
 * One page of nearby people. The headcount is DERIVED, not carried: since the
 * step-10 count removal the screen shows the rendered count, with a "+" only
 * when `hasMore` says somebody beyond the page was PROVED to exist. It used to
 * key that on `nextCursor` — which is why the plus lied twice over, since a
 * cursor is issued by a scan that ran out of budget having found nobody, and a
 * page filling at exactly 75 never looked at a 76th. No field here states the
 * population.
 */
export type NearbyResult = {
  people: NearbyUser[];
  /**
   * Whether somebody BEYOND this page was proved to exist.
   *
   * Read this for the "N+" marker, never `nextCursor`. A cursor only says
   * where a reader resumes, and since a budget-exhausted scan issues one while
   * proving nothing, keying the plus on it made the chip claim more people
   * whenever the read simply had not finished. The server proves this by
   * taking one more presented candidate than it returns.
   *
   * Optional, because a client can outlive a server that has not shipped it;
   * absent reads as "not proved", which is the same conservative answer.
   */
  hasMore?: boolean;
  /**
   * The viewer's own Same Space participation, on the radius list only.
   *
   * A consent read. The mode is in memory and the participation is on the
   * server, so a relaunch forgets one and not the other — this is how the app
   * learns it is still being published in a room and can show it. Absent on a
   * cursored page and on an older API; absent means "not asked", not "none".
   */
  sameSpace?: {
    venueId: string;
    venueName: string;
    /**
     * Whether the participation is currently answering — still inside the
     * joined space. Decides the header LABEL; the participation existing at
     * all decides the disclosure, so somebody who has walked out can still see
     * and clear it.
     */
    active: boolean;
  } | null;
  /**
   * Deprecated wire shim, always equal to `people.length`. It used to be the
   * uncapped population — the query that computed it was 83–92% of every
   * Nearby request and could not be made liveness-correct, so it is gone.
   * Kept on the wire only so clients built before the removal keep rendering
   * a number; nothing may read it anew. Removed with the flip build.
   */
  total: number;
  /**
   * Whether anyone would be visible with the filters off — 1, 0, or null
   * when the question was not worth asking. Numeric for wire compatibility
   * (it used to carry a count); the only read is `(totalUnfiltered ?? 0) > 0`.
   *
   * Only sent when the page is empty AND a filter is set, because that is the one
   * moment the answer changes what the screen should say: "nobody's around" and
   * "nobody matches your filters" are the same zero and opposite advice, and
   * only one of them comes with an action the user can take.
   */
  totalUnfiltered: number | null;
  /**
   * Where the next page starts, or null once the list is exhausted.
   *
   * Opaque to this side on purpose. It is the last row's id, because that is
   * the only key on a nearby row that is both unique and immutable — the server
   * pages on it so that nobody can move between pages while someone scrolls.
   * Treat it as a token: do not parse it, do not sort by it, and do not show it.
   */
  nextCursor: string | null;
};


/**
 * Somebody you have blocked, as shown on the list you can unblock them from.
 *
 * Directional: only ever your own blocks. A blocked person is never told who
 * blocked them, and nobody is shown a name they cannot act on.
 */
export type BlockedUser = {
  id: ID;
  displayName: string;
  avatarSeed: string;
  avatarConfig?: UserAvatarConfig | null;
  blockedAt: string;
};

export type GuestProfile = {
  id: ID;
  displayName: string;
  venueTag?: string | null;
  avatarSeed: string;
  avatarConfig?: UserAvatarConfig | null;
  ageBand?: string | null;
  headline?: string | null;
  interests?: string[] | null;
  lookingFor?: string[] | null;
  languages?: string[] | null;
  /** The same badge everyone else sees beside this name. */
  revealed: boolean;
  // Interests are NOT here, deliberately: they are public — the nearby card,
  // the chat header and this profile's own public half all carry them — so a
  // blurred second copy behind the reveal was a contradiction, not a secret.
  // `religion` was `values` on the wire while the edit screen said Religion;
  // one field, one name now.
  revealDetails?: {
    age?: number | null;
    about?: string | null;
    gender?: string | null;
    religion?: string | null;
    politics?: string | null;
  };
};

/**
 * `ignored` is an active decline by the recipient, not silence — silence stays
 * `pending` until it expires. `cancelled` is the sender withdrawing before any
 * answer, which returns their connection slot. Mirrors the API's CueStatus.
 */
export type CueStatus =
  | "pending"
  | "accepted"
  | "ignored"
  | "blocked"
  | "cancelled"
  | "expired";

/**
 * What `GET /cue/counts` returns: the sender's two allowances plus the count of
 * requests waiting in their inbox.
 *
 * Connections are capacity — held while a cue awaits a reply, spent when one is
 * accepted, returned when a cue is declined, cancelled or expires. Recipients
 * are outreach — every distinct person cued in the window, counted whatever
 * became of the cue, so cancelling frees a slot but not a person.
 */
export type CueCounts = {
  connectionsUsed: number;
  connectionsLimit: number;
  connectionsAvailable: number;
  recipientsUsed: number;
  recipientsLimit: number;
  recipientsAvailable: number;
  /** Requests waiting for *you* — the inbox badge, not an allowance. */
  pending: number;
};

/**
 * Which door a Cue came through, and therefore what the card may honestly say.
 *
 * "Someone near you" is the whole frame of a Nearby Cue and a false statement
 * about the other two: a group Cue can come from three cities away, and an
 * invite Cue comes from a friend who sent a link. Absent reads as `nearby`,
 * which is what every Cue that predates origins actually was.
 */
export type CueOrigin = "nearby" | "small_group" | "invite";

export type Cue = {
  id: ID;
  fromUserId: ID;
  toUserId: ID;
  /** Where the sender stood, for a Nearby Cue. Null for every other origin. */
  venueId: ID | null;
  origin?: CueOrigin;
  status: CueStatus;
  createdAt: string;
  /**
   * The conversation this Cue became, on an accepted Cue in the Sent ledger.
   * Null on everything else — a pending Cue has no chat to open yet.
   */
  chatId?: ID | null;
  /** Convenience display data for inbox rendering. */
  fromName?: string;
  fromAvatarSeed?: string;
  fromAvatarConfig?: UserAvatarConfig | null;
  fromVenueTag?: string | null;
  fromAgeBand?: string | null;
  fromHeadline?: string | null;
  fromInterests?: string[] | null;
  fromLookingFor?: string[] | null;
  /** Convenience display data for the recipient (sent-cue rendering). */
  toName?: string;
  toAvatarSeed?: string;
  toAvatarConfig?: UserAvatarConfig | null;
  toVenueTag?: string | null;
  toAgeBand?: string | null;
  toHeadline?: string | null;
  toInterests?: string[] | null;
  toLookingFor?: string[] | null;
};

/**
 * One bounded section of the inbox.
 *
 * `totalCount` is the section's true size and `sessions` is only what has
 * loaded. They differ as soon as somebody has more conversations than a page,
 * and the tab badge must use the former — it is announced to screen readers as
 * a total.
 */
export type ChatSessionPage = {
  sessions: ChatSession[];
  /** Opaque. Hand it back untouched to read the next page; null means the end. */
  nextCursor: string | null;
  totalCount: number;
};

/** The inbox, in the two sections the Active and Inactive tabs render. */
export type ChatInbox = {
  live: ChatSessionPage;
  inactive: ChatSessionPage;
};

/**
 * No `venueId`. The server keeps one on the row for metrics, but it gates
 * nothing and nothing here ever drew it — carrying it meant every inbox page
 * shipped a place identifier to the device for no purpose. Mirror of
 * `ChatSessionDto`; keep them in step.
 */
export type ChatSession = {
  id: ID;
  userA: ID;
  userB: ID;
  peerId: ID;
  peerName: string;
  peerAvatarSeed: string;
  peerAvatarConfig?: UserAvatarConfig | null;
  peerVenueTag?: string | null;
  peerAgeBand?: string | null;
  peerHeadline?: string | null;
  peerAbout?: string | null;
  peerInterests?: string[] | null;
  peerLookingFor?: string[] | null;
  peerLanguages?: string[] | null;
  peerGender?: string | null;
  peerReligion?: string | null;
  peerPolitics?: string | null;
  lastMessage?: string;
  /**
   * Which app-written line `lastMessage` is, when it is one — render this, not
   * `lastMessage`. Absent for a human message.
   */
  lastMessageKey?: string;
  /**
   * The peer's messages past my read mark, capped server-side at 99. Present
   * only on inbox list responses — the endpoints that answer a single action
   * (reopen, clear) deliberately omit it so a spread-merge cannot zero a
   * badge the list had set.
   */
  unreadCount?: number;
  updatedAt: string;
  revealed: boolean;
  revealStatus?: RevealStatus;
  revealRequestedByMe?: boolean;
  revealRequestedByPeer?: boolean;
  /**
   * False once the conversation stops accepting messages — ended, inactive, or
   * the peer having deactivated their account.
   */
  active?: boolean;
  /**
   * The peer deactivated their account. The thread stays put, with its history
   * and their name; only sending is off, and it returns by itself if they come
   * back. Distinct from `state`, which does not change.
   */
  peerUnavailable?: boolean;
  /**
   * There is nobody to show: a block stands between the two, or the account is
   * gone. The name reads "Someone", the avatar is the neutral one, and every
   * profile field arrives empty — so there is no profile to open, and the
   * server refuses the request if it is made.
   *
   * Both sides of a block see this. Only the side that placed it is told
   * anything more, through `canUnblock`.
   */
  peerHidden?: boolean;
  /** The peer's public badge — shown wherever their name is, blocked or not. */
  /**
   * This person deleted their account. The conversation is still yours to read
   * — your messages are your content too — but there is nobody on the other
   * end, nothing to send, and no route back.
   *
   * Distinct from `peerUnavailable`, which is deactivation and reverses itself
   * if they return. This one does not.
   */
  peerGone?: boolean;
  /**
   * Which inbox tab this belongs in. Separate from `active`, which only says
   * whether the composer is usable: an ended conversation and a dormant one
   * both fail that test, but only one of them offers a way back.
   */
  state?: ChatState;
  /** Why it ended: "blocked" | "reported_blocked" | "reported_ended". */
  endedReason?: string | null;
  /** When it moves to Inactive if neither person replies. */
  inactiveAt?: string;
  /** True in the final 24 hours before that. */
  inactiveWarning?: boolean;
  /** Set once it has actually moved. */
  inactiveSince?: string | null;
  reopenRequestedByMe?: boolean;
  reopenRequestedByPeer?: boolean;
  /**
   * False while a request is pending, while a decline cooldown is running, or
   * when the other person exited quietly and the way back is theirs to offer.
   */
  canRequestReopen?: boolean;
  /**
   * This viewer walked away from this conversation and can walk back into it
   * alone — no request, no acceptance, and nothing sent to the other person,
   * who was never told anything happened.
   *
   * Mutually exclusive with `canRequestReopen`. Which of the two a dormant card
   * offers is what says which kind of dormant it is.
   */
  canResume?: boolean;
  /**
   * Whether this viewer can lift a block from this conversation.
   *
   * True only for the person who placed it. A blocked thread sits in Inactive
   * beside the dormant ones and reads the same way — history intact, nothing
   * writable — and differs only in the action offered at the bottom: Unblock
   * rather than "Ask to reopen". The blocked side gets `false` and no
   * explanation, because being told a decision was made about you is not this
   * card's job.
   */
  canUnblock?: boolean;
  /**
   * True only for the decline cooldown — the one case worth explaining. After a
   * quiet exit this stays false, so the other person sees a plain Inactive
   * thread rather than a hint that a decision was made about them.
   */
  reopenCooldown?: boolean;
  /**
   * When this peer's Open-to-chat signal lapses, or null when they are not
   * open — or when I am not allowed to be told.
   *
   * Present on ACTIVE rows as well as Open ones, deliberately: it is what
   * draws the arc on their avatar in the Active tab, so seeing who is open
   * costs no extra request and no second list. The Open tab is the same rows
   * filtered, not a different source of truth.
   *
   * Null covers three different refusals, and the client does not need to tell
   * them apart: they are not open, the conversation is not writable, or they
   * quietly left it and must not appear available to somebody they walked away
   * from. The server decides all three (`openUntilFor`); the client only ever
   * checks this against the clock.
   */
  peerOpenUntil?: string | null;
};

export type ChatState = "active" | "inactive" | "ended";

/** What a bubble renders. `photo` bubbles have no text at all. */
export type MessageKind = "text" | "system" | "photo";

export type PhotoExchangeStatus =
  | "available"
  | "revealed"
  | "exhausted"
  | "closed";

/**
 * Everything the photo card needs. Note there are no URLs here — only asset
 * ids, which the authenticated image route turns into bytes on demand.
 */
export type PhotoExchangeState = {
  status: PhotoExchangeStatus;
  /** How many of my two requests are left. */
  attemptsLeft: number;
  pending: {
    attemptId: string;
    byMe: boolean;
    expiresAt: string;
  } | null;
  revealed: {
    myAssetId: string | null;
    peerAssetId: string | null;
  } | null;
  canRequest: boolean;
};

/** A picked image, in the shape React Native's FormData wants. */
export type PhotoUpload = {
  uri: string;
  name: string;
  type: string;
  /**
   * The browser's own File handle, present only on web. React Native's
   * `{ uri, name, type }` file part means nothing to a browser FormData, which
   * would stringify it, so on web this is what actually gets sent.
   */
  file?: Blob;
};

/**
 * How a user leaves a conversation.
 *
 * "quiet" no longer ends anything: it moves the thread to Inactive, where
 * nothing is deleted and only the person who left may ask for it back.
 * Clearing your own copy is a separate action entirely — see `chatApi.clear`.
 */
export type EndConversationOutcome = "quiet" | "blocked";

export type ReportReason =
  | "harassment"
  | "inappropriate_messages"
  | "sexual_content"
  | "hate_speech"
  | "threats"
  | "spam_scam"
  | "fake_profile"
  | "underage"
  | "unsafe_in_person"
  | "other";

export type ReportInput = {
  reportedUserId: ID;
  chatId?: ID;
  reason: ReportReason;
  details?: string;
  evidenceUrl?: string;
  /** An exchanged photo this report is about. Hidden from you immediately. */
  photoAssetId?: ID;
  block?: boolean;
  endConversation?: boolean;
  hide?: boolean;
};

export type ChatMessage = {
  id: ID;
  chatId: ID;
  senderId: ID;
  /** Absent on older payloads, which are all plain text. */
  kind?: MessageKind;
  /** Set on `photo` bubbles. An id, not a URL — bytes come from the API. */
  photoAssetId?: ID | null;
  /**
   * The photo's real pixel size, sent with the message so the bubble can be
   * laid out before the bytes arrive. Null on photos uploaded before the
   * server recorded dimensions.
   */
  photoWidth?: number | null;
  photoHeight?: number | null;
  content: string;
  /**
   * Which app-written line this is, on a `system` bubble. The server derives it
   * per viewer from the stored text, so every row ever written carries one;
   * `content` is the English it derived it from and stays as the fallback for a
   * key this bundle does not have. Never set on a human message — those are
   * somebody's own words and are shown exactly as typed.
   */
  systemKey?: string | null;
  timestamp: string;
  reaction?: string | null;
  replyTo?: {
    id: ID;
    senderId: ID;
    content: string;
  } | null;
  /**
   * Set only on bubbles the message outbox is still holding — never on a row
   * from the server. One flag rather than a status, because `queued` and
   * `sending` render identically and there is no failed state to reach
   * (`outbox-core.ts`).
   */
  pending?: boolean;
  /**
   * Whether a pending bubble has waited long enough to show its marker
   * (`SLOW_SEND_AFTER_MS`). Still not a status: it decides whether the marker
   * is drawn, not what the message is.
   */
  pendingSlow?: boolean;
  /**
   * This device's own key for a message it sent, echoed back by the server.
   *
   * The ONE value shared by a pending outbox entry and the saved message it
   * becomes, whichever path delivered it. Absent on anything sent without a key
   * and on every message written before the outbox existed, so it identifies a
   * pending row's counterpart and nothing else.
   */
  clientMessageId?: string | null;
};

// ── Conversation Practice (AI roleplay coach) ─────────────────────────
// Entirely separate from real user-to-user chats. The AI never reads private
// chats — these types describe only the private practice sandbox.

export type PracticeMode = "guided" | "roleplay" | "challenge";
export type PracticeVoice = "calm" | "warm" | "bright" | "grounded";

export type PracticeScenario = {
  id: ID;
  /** Stable key for localized copy + guided openings (not a UUID). */
  slug: string;
  title: string;
  description: string;
  aiRole: string;
  userGoal: string;
};

export type PracticeMessageRole = "user" | "coach" | "system";

/**
 * What the user was doing when they sent the message that produced this reply.
 * Classified server-side and never shown as a label — it only decides how the
 * turn is presented. See backend practice-intent.ts.
 */
export type PracticeIntent =
  | "roleplay"
  | "coach"
  | "draft_review"
  | "debrief"
  | "adjust";

/** In-scene dialogue, or opncue stepping out of the scene to help. */
export type PracticePresentation = "chat" | "practice_tip";

export type PracticeSuggestedActionKind =
  | "continue_scene"
  | "try_response"
  | "show_example"
  | "retry"
  | "adjust_scene";

export type PracticeSuggestedAction = {
  label: string;
  action: PracticeSuggestedActionKind;
};

/** Presentation metadata on a coach reply. Absent = ordinary chat bubble. */
export type PracticeResponseMeta = {
  intent: PracticeIntent;
  presentation: PracticePresentation;
  resumeRoleplay?: boolean;
  suggestedAction?: PracticeSuggestedAction;
};

export type PracticeMessage = {
  id: ID;
  role: PracticeMessageRole;
  content: string;
  createdAt: string;
  /**
   * Coach replies only. Absent on user/system turns and on replies stored
   * before this existed — render those as a normal in-character bubble.
   */
  response?: PracticeResponseMeta;
};

export type PracticeSessionStatus = "active" | "ended" | "abandoned";

/** Where a session's scene comes from: the curated bank, or the user's own. */
export type PracticeSceneSource = "curated" | "byo";

export type PracticeSession = {
  id: ID;
  scenarioId: ID;
  status: PracticeSessionStatus;
  practiceMode: PracticeMode;
  /** "curated" (default) or "byo" (user recounts their own situation). */
  sceneSource?: PracticeSceneSource;
  /** BYO: true once intake produced a scene and the roleplay can begin. */
  byoReady?: boolean;
  startedAt: string;
  endedAt: string | null;
  summaryStatus?:
    | "NOT_REQUESTED"
    | "NOT_ENOUGH_PRACTICE"
    | "GENERATING"
    | "READY"
    | "FAILED_RETRYABLE";
  summaryVersion?: number | null;
  summaryData?: PracticeSummary | null;
  summaryGeneratedAt?: string | null;
};

export type PracticeSummary = {
  version: 2;
  mainTakeaway: string;
  didWell: string[];
  tryNext: {
    guidance: string;
    example?: string | null;
    reason?: string | null;
  };
  realLifeStep: string;
};

export type PracticeWarning = "none" | "five_left" | "two_left" | "empty";

export type PracticeCredits = {
  includedRemaining: number;
  purchasedRemaining: number;
  remainingReplies: number;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  warning: PracticeWarning;
};

/** Protected surfaces a verification requirement can gate. */
export type VerificationFeature =
  | "account"
  | "nearby_visibility"
  | "people_nearby"
  | "cues"
  | "chat"
  | "small_groups"
  | "profile_reveal"
  | "conversation_practice"
  | "reflections"
  | "goals"
  | "progress";

/** Safe age-verification policy for the current user (no DOB/provider data). */
export type AgeVerificationPolicy = {
  status: string;
  minimumAgeRequired: number;
  ageBand?: string;
  strongerVerificationRequired: boolean;
  canUseApp: boolean;
  blockedFeatures: VerificationFeature[];
  reasonCode?: string;
};

export type PaymentMethodSummary = {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
};

/**
 * WHERE the subscription lives, which decides who can change it. `"store"`
 * means App Store / Play: no price and no ability to cancel it from here.
 * `currentPeriodEnd` IS filled for both providers — it is the date the
 * subscription stops paying for Plus, whoever is collecting the money.
 */
export type SubscriptionProvider = "stripe" | "store";

export type SubscriptionSummary = {
  active: boolean;
  plan: "free" | "plus";
  /** Absent on free. */
  provider?: SubscriptionProvider;
  status?: string;
  /** Charge amount in minor units for `currency`. */
  amount?: number;
  currency?: string;
  interval?: "month" | "year";
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  paymentMethod?: PaymentMethodSummary | null;
};

/**
 * Billing address collected at checkout and handed to Stripe. `country` is what
 * Stripe Tax prices against; the rest is what Stripe needs to charge the card
 * and to render the invoice.
 */
export type BillingAddress = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country: string;
};

/** One row in the in-app checkout order summary. */
export type SubscriptionPreviewLine = {
  label: string;
  /** Amount in minor units for the preview's `currency`. */
  amount: number;
};

/**
 * Server-priced order summary for the in-app subscribe page. Every figure is
 * computed by Stripe, never client-side, so the total shown is always the total
 * charged.
 */
export type SubscriptionPreview = {
  lines: SubscriptionPreviewLine[];
  subtotal: number;
  /** 0 until Stripe Tax is enabled; the summary hides the row in that case. */
  tax: number;
  /** True when tax is bundled into `total` rather than added on top of it. */
  taxInclusive: boolean;
  total: number;
  currency: string;
  interval: "month" | "year";
  nextBillingDate?: string;
  /**
   * Present when this buyer gets a free trial: `total` is 0 (nothing due
   * today) and `amount` recurs per `interval` from `firstChargeAt`.
   */
  trial?: {
    days: number;
    /** Minor units per interval after the trial, in `currency`. */
    amount: number;
    /** ISO date of the first real charge. */
    firstChargeAt?: string;
  };
};

/** Result of starting an in-app subscription. */
export type SubscriptionStart = {
  subscriptionId: string;
  /** Present when the client must confirm a payment to activate the plan. */
  clientSecret?: string;
  /**
   * "payment" confirms the first invoice (confirmPayment); "setup" saves the
   * card for a free trial's first charge (confirmSetup).
   */
  clientSecretType?: "payment" | "setup";
  status: string;
};

/** Client-safe scenario-pack content for display (no internal metadata). */
export type PracticePackView = {
  packId: ID;
  locale: string;
  scene: string;
  goal: string;
  visibleSocialCue: string;
  starterOptions: string[];
};

/** One turn of the BYO setup conversation (kept out of the roleplay transcript). */
export type ByoIntakeTurn = { role: "user" | "guide"; content: string };

/**
 * Outcome of a BYO intake turn. The guide either is still gathering or has
 * handed off a scene; refusal/redirect/care are expressed as ordinary
 * conversation, so they carry no distinct status (the client only reads `ready`).
 */
export type ByoIntakeStatus = "gathering" | "ready";

/** Response from POST /practice/sessions/:id/intake. */
export type ByoIntakeResult = {
  status: ByoIntakeStatus;
  /** What to show the user, in opncue's own voice. */
  say: string;
  /** True once a scene was captured and the roleplay can begin. */
  ready: boolean;
  session: PracticeSession;
  /** Balance after this turn (a reply is spent from the 2nd AI call onward). */
  credits: PracticeCredits;
};

export type PracticeSessionView = {
  session: PracticeSession;
  messages: PracticeMessage[];
  pack: PracticePackView | null;
  /** BYO setup conversation so far (empty for curated sessions). */
  intake?: ByoIntakeTurn[];
};

export type PracticeSendResult = {
  message: PracticeMessage;
  credits: PracticeCredits;
  warning: PracticeWarning;
};

export type PracticeSummaryResponse =
  | {
      status: "ready";
      session: PracticeSession;
      summary: PracticeSummary;
    }
  | {
      status: "not_enough_practice";
      reason: "no_practice" | "too_short";
      session: PracticeSession;
    }
  | {
      status: "failed_retryable";
      session: PracticeSession;
    };

export type PracticePack = {
  id: string;
  replies: number;
  /** What Stripe charges on web. On native the STORE's price is authoritative. */
  price: number;
  label: string;
  /**
   * The store product behind this pack, per platform. Served by the API so the
   * id table has exactly one home (the server) and cannot drift out of step
   * with the App Store / Play listings.
   */
  storeProductIds?: { ios: string; android: string };
};

/** A Help & Support ticket submitted from the in-app contact form. */
export type SupportInput = {
  category: string;
  message: string;
  responseEmail: string;
};

export type SupportSubmitResult = {
  id: string;
  status: string;
};

/** Where one of the caller's outgoing referrals is in the qualify→vest funnel. */
export type ReferralStatus = "pending" | "qualified" | "vested" | "rejected";

export type ReferralProgress = {
  id: ID;
  status: ReferralStatus;
  createdAt: string;
  qualifiedAt: string | null;
  vestedAt: string | null;
};

/** The "Invite a friend" screen payload from GET /referrals/me. */
export type InviteView = {
  code: string;
  inviteUrl: string;
  rewardedThisMonth: number;
  monthlyCap: number;
  referrals: ReferralProgress[];
};

export type RevealStatus = "idle" | "requested" | "pending_peer" | "revealed";

export type RevealResult = {
  firstName: string;
  avatarUrl?: string | null;
  proximityHint?: string;
  // Mirrors `RevealResultDto` on the API — no interests (public everywhere),
  // and `religion` rather than `values`, matching the edit screen's own name.
  sharedDetails?: {
    age?: number | null;
    about?: string | null;
    gender?: string | null;
    religion?: string | null;
    politics?: string | null;
  };
};

/**
 * What the viewer wants out of the groups they could join.
 *
 * Distinct from eligibility, which the server applies on its own: a 20-50 group
 * will accept a 30-year-old, and this is how they say they only want groups
 * aimed at 20-30. Sent to the server, never applied to the response.
 */
export type GroupBrowseFilters = {
  vibe?: string;
  maxMembers?: number;
  ageBandMinFloor?: number;
  ageBandMaxFloor?: number;
};

/**
 * An invite link that carries its sender's intent.
 *
 * Distinct from the referral code on the Invite screen, which is stable, reused
 * for life, and only attributes genuinely new signups. This is single-use,
 * expires, and works for a friend who already has the app — because "come and
 * talk to me" is a thing you say to one person on one day.
 */
export type MintedInvite = {
  code: string;
  url: string;
  cueIntent: boolean;
  expiresAt: string;
};

/** Why a link cannot be claimed. `ok` is the only state that offers a button. */
export type InviteState = "ok" | "expired" | "already_used" | "your_own";

export type InvitePreview = {
  code: string;
  inviterName: string;
  inviterAvatarSeed: string;
  cueIntent: boolean;
  state: InviteState;
};

/**
 * What claiming an invite produced.
 *
 * Named rather than inferred from which of `cueId`/`chatId` is null, because
 * two very different states share `both null`: a pair who have talked before
 * but whose conversation has ended, and a refusal the person may not be told
 * the reason for. Only the first earns an explanation.
 */
export type InviteOutcome = "cue_sent" | "already_connected" | "unavailable";

export type InviteRedemption = {
  outcome: InviteOutcome;
  /** Whether the referral attribution took. False for an existing account. */
  referralApplied: boolean;
  /** The Cue the sender's intent earned, if it earned one. */
  cueId: ID | null;
  /**
   * The conversation these two already have, when that is why no Cue was
   * minted. The friend you most want to say "come and talk to me" to is often
   * the one you already talk to.
   */
  chatId: ID | null;
};
